import { useIntervalFn, useDocumentVisibility } from '@vueuse/core'
import type { UseIntervalFnOptions } from '@vueuse/core'

/**
 * Repeatedly call an async function on a fixed interval and expose the latest result.
 * Polling is automatically paused when the browser tab becomes inactive and resumed
 * when it becomes visible again, so background tabs don't generate unnecessary
 * requests. No cleanup is needed - it's handled natively in useIntervalFn().
 *
 * If we start using this composable for heavier/slower endpoints (requests start overlapping)
 * or we need fine-tuned control over cancellation, consider migrating to AbortController + setTimeout.
 */
export function usePolling<T>(fetchFn: () => Promise<T>, options: UseIntervalFnOptions = {}, interval = 5000) {
  const data = ref<T | null>(null)
  const isLoading = ref(false)
  const error = ref<Error | null>(null)

  async function fetchData() {
    isLoading.value = true
    try {
      data.value = await fetchFn()
      error.value = null
    } catch (e) {
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  const { pause, resume, isActive } = useIntervalFn(fetchData, interval, options)

  const isDocumentVisible = useDocumentVisibility()

  // Save the state before pausing, so returning to the tab resumes polling
  // without overriding a manual pause()
  let wasActiveBeforeHide = false

  watch(isDocumentVisible, newState => {
    if (newState === 'hidden') {
      wasActiveBeforeHide = isActive.value
      pause()
    } else if (newState === 'visible' && wasActiveBeforeHide) {
      resume()
    }
  })

  return {
    data,
    error,
    isLoading,
    isActive,
    pause,
    resume,
  }
}
